import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import axiosInstance from "@/lib/axios";
import { leadSourceEndpoint } from "@/config/endpoints";

const PAGE_SIZE = 24;

function cleanQueryParams(params: Record<string, any>) {
  return Object.fromEntries(
    Object.entries(params).filter(
      ([, v]) =>
        v !== undefined &&
        v !== null &&
        v !== "" &&
        !(Array.isArray(v) && v.length === 0)
    )
  );
}

export default function useLeadSource(
  paramsOrCallback?: Record<string, any> | ((data: any) => void),
  callBack?: (data: any) => void
) {
  // Back-compat: callers used to pass `useLeadSource(callBack)`.
  const params: Record<string, any> =
    typeof paramsOrCallback === "function" || paramsOrCallback == null
      ? {}
      : paramsOrCallback;
  const cb =
    typeof paramsOrCallback === "function" ? paramsOrCallback : callBack;

  const cleanParams = cleanQueryParams(params);

  const query = useQuery({
    queryKey: ["lead-sources", cleanParams],
    queryFn: async () => {
      const res = await axiosInstance.get(leadSourceEndpoint, {
        params: cleanParams,
      });
      return {
        sources: res.data.sources ?? [],
        meta: res.data.meta,
      };
    },
  });

  useEffect(() => {
    if (query.data?.sources && cb) {
      cb(query.data.sources);
    }
  }, [query.data?.sources]);

  const sourceSelects =
    query.data?.sources?.map((val: any) => ({
      value: String(val.id),
      name: val.name,
    })) ?? [];

  const isCmi = query.data?.sources?.some((val: any) => val.type === "TELECMI");

  return {
    leadSources: query.data?.sources,
    sourceSelects,
    length: Number(query.data?.meta?.total) || query.data?.sources?.length || 0,
    isLoading: query.isLoading,
    error: query.error,
    isCmi,
    refetch: query.refetch,
  };
}

export function useInfiniteLeadSources(
  params: Record<string, any> = {},
  pageSize = PAGE_SIZE
) {
  const cleanParams = cleanQueryParams(params);

  const query = useInfiniteQuery({
    queryKey: ["lead-sources", "infinite", cleanParams, pageSize],
    queryFn: async ({ pageParam = 1 }) => {
      const res = await axiosInstance.get(leadSourceEndpoint, {
        params: {
          ...cleanParams,
          page: pageParam,
          limit: pageSize,
        },
      });
      return res.data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage: any, allPages) => {
      const total = Number(lastPage?.meta?.total) || 0;
      const loaded = allPages.flatMap((page: any) => page.sources ?? []).length;
      if (loaded > 0 && loaded < total) return allPages.length + 1;
      const lastBatch = (lastPage?.sources ?? []).length;
      if (!total && lastBatch === pageSize) return allPages.length + 1;
      return undefined;
    },
    select: (data) => {
      const leadSources = data.pages.flatMap((page: any) => page.sources ?? []);
      return {
        leadSources,
        totalCount: Number(data.pages[0]?.meta?.total) || leadSources.length,
      };
    },
  });

  return {
    leadSources: query.data?.leadSources ?? [],
    totalCount: query.data?.totalCount ?? 0,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    fetchNextPage: query.fetchNextPage,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
  };
}
